import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  CreditCard,
  Shield,
  Clock,
  CheckCircle,
  AlertCircle,
} from "lucide-react";
import axios from "axios";

import Api from "../Api/Api.json";

const environment = process.env.NODE_ENV === "production"
  ? "production"
  : "development";

const Payment = () => {
  const location = useLocation();
  const [cost, setCost] = useState(null);
  const [isConfirming, setIsConfirming] = useState(false);
  const [patientId, setPatientId] = useState(0);
  const [isPrepopulated, setIsPrepopulated] = useState(false);
  const [shippingAddress, setShippingAddress] = useState('');
  const [shippingPhone, setShippingPhone] = useState('');

  useEffect(() => {
    const storedInfo = localStorage.getItem("user_info");
    if (storedInfo !== null) {
      const userInfo = JSON.parse(storedInfo);
      setPatientId(userInfo.di_id);
    }
  }, []);

  useEffect(() => {
    // Prepopulate amount from navigation state if available
    if (location.state && location.state.amount) {
      setCost(location.state.amount);
      setIsPrepopulated(true);
    }
  }, [location.state]);


  const createPaymentUpskill = async (token_id) => {
    const num = patientId + new Date().getTime();

    const body = {
      task: "createPaymentBackendUpskill",
      amount: cost.toString(),
      merchantInvoiceNumber: num.toString(),
      environment: environment,
      app:"semionhealth"
    };

    try {
      setIsConfirming(true);
      const headers = { "Content-Type": "application/json" };
      const res = await axios.post(Api.BKASH_API, body, { headers });
      console.log(res.data);
      if (!res.data.errorMessage && res.data.message !== "Unauthorized") {
        const matches =
          res.data.merchantInvoiceNumber === num.toString() &&
          res.data.amount.toString() === cost.toString() &&
          res.data.intent === "sale";

        if (matches) {
          window.location = res.data.bkashURL; // Redirect to bKash payment page
        } else {
          console.error("Payment details do not match");
        }
      } else {
        console.error("Unauthorized access or error in payment creation");
      }
    } catch (err) {
      console.error("Error creating payment:", err);
    } finally {
      setIsConfirming(false);
    }
  };

  const handlePayment = () => {
    if (cost <= 0) {
      alert("Please enter a valid amount");
      return;
    }
    if (!shippingAddress.trim()) {
      alert("Please enter shipping address");
      return;
    }
    if (!shippingPhone.trim()) {
      alert("Please enter phone number");
      return;
    }
    // Store shipping info in localStorage for invoice
    localStorage.setItem('shipping_info', JSON.stringify({
      address: shippingAddress,
      phone: shippingPhone,
      amount: cost,
      timestamp: new Date().toISOString()
    }));
    createPaymentUpskill();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 pt-20 pb-4 px-4 flex items-center justify-center">
      <div className="w-full max-w-md">
        {/* Main Payment Card */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-6 text-white">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-white/20 rounded-lg">
                <CreditCard className="w-6 h-6" />
              </div>
              <div>
                <h2 className="text-xl font-bold">Secure Payment</h2>
                <p className="text-blue-100 text-sm">
                  Complete your transaction
                </p>
              </div>
            </div>
          </div>

          {/* Payment Content */}
          <div className="p-6 space-y-6">
            {/* Amount Input */}
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700">
                Payment Amount
                {isPrepopulated && (
                  <span className="ml-2 text-xs text-gray-500 font-normal">(Pre-filled from product selection)</span>
                )}
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 font-medium">
                  ৳
                </span>
                <input
                  type="number"
                  value={cost || ''}
                  onChange={(e) => !isPrepopulated && setCost(Number(e.target.value))}
                  readOnly={isPrepopulated}
                  disabled={isPrepopulated}
                  className={`w-full pl-8 pr-4 py-3 border border-gray-300 rounded-xl transition-all duration-200 text-lg font-semibold ${
                    isPrepopulated 
                      ? 'bg-gray-100 cursor-not-allowed text-gray-700' 
                      : 'focus:ring-2 focus:ring-blue-500 focus:border-transparent'
                  }`}
                  placeholder="0.00"
                  min="0"
                  step="1"
                />
              </div>
            </div>

            {/* Payment Method Selection */}
            <div className="space-y-3">
              <label className="block text-sm font-semibold text-gray-700">
                Payment Method
              </label>
              <div className="grid grid-cols-1 gap-3">
                <div className="p-4 border-2 rounded-xl cursor-pointer transition-all duration-200border-pink-500 bg-pink-50">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-pink-500 rounded-lg flex items-center justify-center">
                      <span className="text-white font-bold text-sm">bK</span>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-800">bKash</p>
                      <p className="text-sm text-gray-500">
                        Mobile Financial Service
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Security Features */}
            <div className="bg-gray-50 rounded-xl p-4">
              <div className="flex items-start space-x-3">
                <Shield className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-semibold text-gray-800">
                    Secure Payment
                  </p>
                  <p className="text-xs text-gray-600 mt-1">
                    Your payment is protected with bank-level security and
                    encryption
                  </p>
                </div>
              </div>
            </div>

            {/* Shipping Information */}
            {isPrepopulated && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900">Shipping Information</h3>
                
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700">
                    Shipping Address <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={shippingAddress}
                    onChange={(e) => setShippingAddress(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 resize-none"
                    placeholder="Enter your complete shipping address"
                    rows="3"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700">
                    Phone Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    value={shippingPhone}
                    onChange={(e) => setShippingPhone(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                    placeholder="Enter your phone number"
                    required
                  />
                </div>
              </div>
            )}

            {/* Payment Summary */}
            {cost > 0 && (
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Total Amount:</span>
                  <span className="text-lg font-bold text-blue-600">
                    ৳{cost.toFixed(2)}
                  </span>
                </div>
              </div>
            )}

            {/* Payment Button */}
            <button
              onClick={handlePayment}
              disabled={isConfirming || cost <= 0 || (isPrepopulated && (!shippingAddress.trim() || !shippingPhone.trim()))}
              className={`w-full py-4 px-6 rounded-xl font-semibold text-white transition-all duration-300 transform ${
                isConfirming || cost <= 0 || (isPrepopulated && (!shippingAddress.trim() || !shippingPhone.trim()))
                  ? "bg-gray-400 cursor-not-allowed scale-95"
                  : "bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 hover:scale-105 shadow-lg hover:shadow-xl"
              }`}
            >
              {isConfirming ? (
                <div className="flex items-center justify-center space-x-2">
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>Processing Payment...</span>
                </div>
              ) : (
                <div className="flex items-center justify-center space-x-2">
                  <CreditCard className="w-5 h-5" />
                  <span>Pay with bKash</span>
                </div>
              )}
            </button>

            {/* Payment Steps Indicator */}
            {isConfirming && (
              <div className="space-y-3 pt-4 border-t border-gray-200">
                <div className="flex items-center space-x-3 text-sm">
                  <div className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center">
                    <CheckCircle className="w-4 h-4 text-white" />
                  </div>
                  <span className="text-gray-600">
                    Validating payment details
                  </span>
                </div>
                <div className="flex items-center space-x-3 text-sm">
                  <div className="w-6 h-6 border-2 border-blue-600 rounded-full flex items-center justify-center">
                    <div className="w-2 h-2 bg-blue-600 rounded-full animate-pulse"></div>
                  </div>
                  <span className="text-gray-600">
                    Connecting to payment gateway
                  </span>
                </div>
                <div className="flex items-center space-x-3 text-sm opacity-50">
                  <div className="w-6 h-6 border-2 border-gray-300 rounded-full flex items-center justify-center">
                    <Clock className="w-4 h-4 text-gray-400" />
                  </div>
                  <span className="text-gray-400">Redirecting to bKash</span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="mt-6 text-center text-xs text-gray-500">
          <p>Powered by bKash • Secure Payment Gateway</p>
          <p className="mt-1">Your transaction is protected and encrypted</p>
        </div>
      </div>
    </div>
  );
};

export default Payment;
